import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { IconEye, IconEyeOff, IconAlertTriangle, IconSparkle } from "../components/icons";
import loginBg from "../assets/images/login/siag-login-bg.webp";

/**
 * Geometria da arte (siag-login-bg). A arte tem uma MOLDURA dourada/vidro vazia
 * no lado direito — o card real é ancorado no centro dela.
 *
 * Ajuste fino (caso necessário):
 *  - ANCHOR_X menor (64–65) move o card p/ a esquerda · maior (67–68) p/ a direita
 *  - ANCHOR_Y menor (48–49) sobe o card
 */
const ART_ASPECT = 1672 / 941; // ≈ 1.7768
const ANCHOR_X = 66;           // % horizontal do card (afastado da borda dourada esquerda)
const ANCHOR_Y = 50.5;         // % vertical

// largura/altura da camada que replica `background-size: cover; position: center`
const LAYER_W = `max(100vw,calc(100vh*${ART_ASPECT}))`;
const LAYER_H = `max(100vh,calc(100vw/${ART_ASPECT}))`;

export function LoginPage() {
  const { user, signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signIn({ email, password });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao entrar");
    } finally {
      setLoading(false);
    }
  }

  const formProps = {
    email, setEmail, password, setPassword,
    showPassword, setShowPassword, error, loading, onSubmit,
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-sistolica-900">
      {/* ── Camada da arte: dimensão = cover+center com a proporção exata ─────── */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 bg-cover bg-center"
        style={{ width: LAYER_W, height: LAYER_H, backgroundImage: `url(${loginBg})` }}
      >
        {/* overlay sutil — leve no mobile, transparente no desktop p/ a arte viva */}
        <div className="absolute inset-0 bg-sistolica-900/45 sm:bg-sistolica-900/30 lg:bg-transparent" />

        {/* Desktop/notebook: ÚNICO card real, centralizado na moldura da arte */}
        <div
          className="hidden lg:block absolute z-10"
          style={{
            left: `${ANCHOR_X}%`,
            top: `${ANCHOR_Y}%`,
            width: "clamp(336px, 22vw, 384px)",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoginForm idPrefix="d" {...formProps} />
        </div>
      </div>

      {/* Mobile/tablet retrato: card centralizado na tela (abandona a moldura) */}
      <div className="lg:hidden absolute inset-0 z-10 flex items-center justify-center p-6">
        <div className="w-[min(380px,92vw)]">
          <LoginForm idPrefix="m" {...formProps} />
        </div>
      </div>
    </div>
  );
}

// ── Formulário (reutilizado em desktop e mobile, ids distintos) ────────────────

interface FormProps {
  idPrefix: string;
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  showPassword: boolean; setShowPassword: (fn: (v: boolean) => boolean) => void;
  error: string | null;
  loading: boolean;
  onSubmit: (e: FormEvent) => void;
}

function LoginForm({
  idPrefix, email, setEmail, password, setPassword,
  showPassword, setShowPassword, error, loading, onSubmit,
}: FormProps) {
  const emailId = `${idPrefix}-email`;
  const passId = `${idPrefix}-password`;

  return (
    <form
      onSubmit={onSubmit}
      className="w-full bg-white rounded-2xl shadow-card-lg ring-1 ring-black/5 p-7"
    >
      {/* identificação da marca */}
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-9 h-9 rounded-lg bg-gold-500 grid place-items-center text-sistolica-800 shadow-sm">
          <IconSparkle className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-sistolica-400">Sistólica</div>
          <div className="text-sm font-semibold text-sistolica-800">Inteligência SIAG</div>
        </div>
      </div>

      <h2 className="text-xl font-semibold text-sistolica-800 tracking-tight">
        Bem-vindo de volta
      </h2>
      <p className="text-sm text-sistolica-500 mt-1">
        Acesse o painel com suas credenciais.
      </p>

      {/* erro */}
      {error && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2.5 text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3.5 py-2.5"
        >
          <IconAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* e-mail */}
      <div className="mt-5">
        <label htmlFor={emailId} className="block text-sm font-medium text-sistolica-700 mb-1.5">
          E-mail
        </label>
        <input
          id={emailId}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@empresa.com.br"
          className="w-full rounded-lg border border-sistolica-200 bg-sistolica-50/40 px-3.5 py-2.5 text-sistolica-800 placeholder:text-sistolica-300 transition-colors focus:outline-none focus:ring-2 focus:ring-sistolica-500 focus:border-transparent focus:bg-white"
          autoComplete="email"
          required
        />
      </div>

      {/* senha */}
      <div className="mt-4">
        <label htmlFor={passId} className="block text-sm font-medium text-sistolica-700 mb-1.5">
          Senha
        </label>
        <div className="relative">
          <input
            id={passId}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full rounded-lg border border-sistolica-200 bg-sistolica-50/40 px-3.5 py-2.5 pr-11 text-sistolica-800 placeholder:text-sistolica-300 transition-colors focus:outline-none focus:ring-2 focus:ring-sistolica-500 focus:border-transparent focus:bg-white"
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            title={showPassword ? "Ocultar senha" : "Mostrar senha"}
            className="absolute inset-y-0 right-0 px-3 flex items-center text-sistolica-400 hover:text-sistolica-600 transition-colors"
          >
            {showPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* botão */}
      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full rounded-lg bg-gold-600 text-white font-semibold py-2.5 shadow-sm transition-all hover:bg-gold-700 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading && (
          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        )}
        {loading ? "Entrando…" : "Entrar"}
      </button>

      <p className="text-center text-xs text-sistolica-400 mt-5">
        Acesso restrito · Powered by Sistólica
      </p>
    </form>
  );
}
